import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, PartialTheme, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { HelpProvider } from './context/HelpContext';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { BestellungenListe } from './pages/BestellungenListe';
import { BestellungDetail } from './pages/BestellungDetail';
import { ArchivListe } from './pages/ArchivListe';
import { ArtikelListe } from './pages/ArtikelListe';
import { KreditorenListe } from './pages/KreditorenListe';
import { MengeneinheitenListe } from './pages/MengeneinheitenListe';
import { FreigabenListe } from './pages/FreigabenListe';
import { Einstellungen } from './pages/Einstellungen';
import { HandbuchPage } from './pages/HandbuchPage';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Stack
        verticalAlign="center"
        horizontalAlign="center"
        styles={{ root: { height: '100vh' } }}
      >
        <Spinner size={SpinnerSize.large} label="Lade..." />
      </Stack>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Inner App Component mit Auth-Zugriff
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Stack
        verticalAlign="center"
        horizontalAlign="center"
        styles={{ root: { height: '100vh' } }}
      >
        <Spinner size={SpinnerSize.large} label="Lade Authentifizierung..." />
      </Stack>
    );
  }

  return (
    <ThemeProvider theme={sharePointTheme}>
      <Router>
        <Routes>
          {/* Public Route */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/bestellungen" replace /> : <Login />}
          />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/bestellungen" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bestellungen"
            element={
              <ProtectedRoute>
                <Layout>
                  <BestellungenListe />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bestellungen/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <BestellungDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/archiv"
            element={
              <ProtectedRoute>
                <Layout>
                  <ArchivListe />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/archiv/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <BestellungDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/artikel"
            element={
              <ProtectedRoute>
                <Layout>
                  <ArtikelListe />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/kreditoren"
            element={
              <ProtectedRoute>
                <Layout>
                  <KreditorenListe />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mengeneinheiten"
            element={
              <ProtectedRoute>
                <Layout>
                  <MengeneinheitenListe />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/freigaben"
            element={
              <ProtectedRoute>
                <Layout>
                  <FreigabenListe />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/einstellungen"
            element={
              <ProtectedRoute>
                <Layout>
                  <Einstellungen />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Handbuch Druckseite */}
          <Route
            path="/handbuch"
            element={
              <ProtectedRoute>
                <HandbuchPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

// SharePoint-ähnliches Theme
const sharePointTheme: PartialTheme = {
  palette: {
    themePrimary: '#0078d4',
    themeLighterAlt: '#eff6fc',
    themeLighter: '#deecf9',
    themeLight: '#c7e0f4',
    themeTertiary: '#71afe5',
    themeSecondary: '#2b88d8',
    themeDarkAlt: '#106ebe',
    themeDark: '#005a9e',
    themeDarker: '#004578',
    neutralLighterAlt: '#faf9f8',
    neutralLighter: '#f3f2f1',
    neutralLight: '#edebe9',
    neutralQuaternaryAlt: '#e1dfdd',
    neutralQuaternary: '#d0d0d0',
    neutralTertiaryAlt: '#c8c6c4',
    neutralTertiary: '#a19f9d',
    neutralSecondary: '#605e5c',
    neutralPrimaryAlt: '#3b3a39',
    neutralPrimary: '#323130',
    neutralDark: '#201f1e',
    black: '#000000',
    white: '#ffffff',
  }
};

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <HelpProvider>
        <AppContent />
      </HelpProvider>
    </AuthProvider>
  );
};
